export const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };
/* -------------------------------------------------------------------------
 * Script detection
 *
 * Imported listings mix Burmese, English and emoji. Marking Burmese runs with
 * `lang="my"` lets the browser pick the Myanmar font and apply script-aware
 * typography without changing the stored copy.
 * ---------------------------------------------------------------------- */
const MYANMAR_SCRIPT = /[\u1000-\u109F\uA9E0-\uA9FF\uAA60-\uAA7F]/u;
export function isMyanmarText(value) {
    return typeof value === "string" && MYANMAR_SCRIPT.test(value);
}
/** React-friendly `lang` value: `"my"` for Burmese content, omitted otherwise. */
export function textLang(value) {
    return isMyanmarText(value) ? "my" : undefined;
}
/* -------------------------------------------------------------------------
 * MMK price formatting
 *
 * Canonical storage is a whole kyat amount encoded as a decimal string in
 * `properties.price_amount` (currency `MMK`). Never store a display label.
 * All conversion is done with BigInt so large property prices never pass
 * through a lossy floating-point representation.
 *
 * A lakh (သိန်း) is exactly 100,000 kyat. "သိန်းကြီး" is deliberately not
 * used: it has no single unambiguous conversion and would mislead buyers.
 * ---------------------------------------------------------------------- */
export const MMK_PER_LAKH = 100000n;
const MYANMAR_DIGITS = ["၀", "၁", "၂", "၃", "၄", "၅", "၆", "၇", "၈", "၉"];
/** Parse a non-negative whole-kyat amount from the database/query string. */
export function parseMmkAmount(value) {
    if (value === null || value === undefined)
        return null;
    if (typeof value === "bigint")
        return value >= 0n ? value : null;
    if (typeof value === "number") {
        if (!Number.isSafeInteger(value) || value < 0)
            return null;
        return BigInt(value);
    }
    const normalized = value.trim().replace(/,/g, "");
    if (!/^\d+$/.test(normalized))
        return null;
    try {
        return BigInt(normalized);
    }
    catch {
        return null;
    }
}
export function toMyanmarDigits(value) {
    return value.replace(/\d/g, (digit) => MYANMAR_DIGITS[Number(digit)]);
}
function groupWithCommas(value) {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
/** Format an integer with Latin digits and thousands separators. */
export function formatLatinInteger(value) {
    const parsed = parseMmkAmount(value);
    if (parsed === null)
        return "";
    return groupWithCommas(parsed.toString());
}
/** Format an integer with Myanmar digits and thousands separators. */
export function formatMyanmarInteger(value) {
    const latin = formatLatinInteger(value);
    return toMyanmarDigits(latin);
}
/** Convert a canonical kyat amount to a decimal lakhs string (no unit). */
export function mmkAmountToLakhs(amount) {
    const parsed = parseMmkAmount(amount);
    if (parsed === null)
        return "";
    const whole = parsed / MMK_PER_LAKH;
    const remainder = parsed % MMK_PER_LAKH;
    if (remainder === 0n)
        return whole.toString();
    const fraction = remainder.toString().padStart(5, "0").replace(/0+$/, "");
    return `${whole}.${fraction}`;
}
/** Convert a decimal lakhs value to canonical whole kyat without float math. */
export function lakhsToMmkAmount(lakhs) {
    const normalized = lakhs.trim().replace(/,/g, "");
    if (!/^\d+(?:\.\d{1,5})?$/.test(normalized))
        return null;
    const [whole, fraction = ""] = normalized.split(".");
    return (BigInt(whole) * MMK_PER_LAKH + BigInt(fraction.padEnd(5, "0"))).toString();
}
/** Format a lakhs value for a filter label, e.g. 5000 -> "သိန်း ၅,၀၀၀". */
export function formatLakhsLabel(lakhs) {
    const parsed = parseMmkAmount(typeof lakhs === "number" ? BigInt(Math.trunc(lakhs)) : lakhs);
    if (parsed === null)
        return "";
    return `သိန်း ${formatMyanmarInteger(parsed)}`;
}
export function formatPropertyPriceParts(property) {
    const amount = parseMmkAmount(property.priceAmount);
    if (property.priceOnRequest) {
        return {
            primary: "ဈေးနှုန်း ညှိနှိုင်း",
            secondary: "Negotiable",
            isOnRequest: true,
            isNegotiable: true,
            amountMmk: null
        };
    }
    if (amount === null || amount === 0n) {
        return {
            primary: "ဈေးနှုန်း မေးမြန်းပါ",
            secondary: "Price on request",
            isOnRequest: true,
            isNegotiable: false,
            amountMmk: null
        };
    }
    if (property.priceCurrency !== "MMK") {
        const latin = formatLatinInteger(amount);
        return {
            primary: `${latin} ${property.priceCurrency}`,
            secondary: "",
            isOnRequest: false,
            isNegotiable: false,
            amountMmk: amount
        };
    }
    const primary = amount >= MMK_PER_LAKH ? formatMmkInLakhs(amount) : `ကျပ် ${formatMyanmarInteger(amount)}`;
    return {
        primary,
        secondary: `MMK ${formatLatinInteger(amount)}`,
        isOnRequest: false,
        isNegotiable: false,
        amountMmk: amount
    };
}
/** Format a whole kyat amount using the natural Myanmar lakh convention. */
export function formatMmkInLakhs(amount) {
    const parsed = parseMmkAmount(amount);
    if (parsed === null)
        return "";
    if (parsed === 0n)
        return "ကျပ် ၀";
    if (parsed < MMK_PER_LAKH)
        return `ကျပ် ${formatMyanmarInteger(parsed)}`;
    const whole = parsed / MMK_PER_LAKH;
    const remainder = parsed % MMK_PER_LAKH;
    if (remainder === 0n)
        return `သိန်း ${formatMyanmarInteger(whole)} ကျပ်`;
    // Fractional lakhs are rare in listings but must stay exact. Five decimal
    // places represent single kyat at lakh scale.
    const fraction = remainder.toString().padStart(5, "0").replace(/0+$/, "");
    return `သိန်း ${toMyanmarDigits(whole.toString())}.${toMyanmarDigits(fraction)} ကျပ်`;
}
/** Backwards-compatible single-line helper used by cards and admin tables. */
export function formatPropertyPrice(property) {
    return formatPropertyPriceParts(property).primary;
}
/** Format a numeric filter boundary (stored in lakhs) for display. */
export function formatPriceFilterSummary(minLakhs, maxLakhs) {
    const min = minLakhs ? formatLakhsLabel(minLakhs) : "";
    const max = maxLakhs ? formatLakhsLabel(maxLakhs) : "";
    if (min && max)
        return `${min} – ${max}`;
    if (min)
        return `${min} မှ`;
    if (max)
        return `${max} အထိ`;
    return "";
}
export * from './editorial.js';
export * from "./policies.js";
